import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { name: 'ESTRUTURA', path: '/quem-somos', id: 'estrutura' },
    { name: 'ESPECIALISTAS', path: '/quem-somos', id: 'especialistas' },
    { name: 'BLOG', path: '/resultados', id: 'blog' },
    { name: 'CONTATO', path: '/contato', id: 'contato' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg py-3' 
          : 'bg-white/80 backdrop-blur-sm py-5'
      }`}
    >
      <div className="container-custom">
        <div className="grid grid-cols-3 items-center">
          
          {/* Logo - Left */}
          <Link 
            to="/" 
            className="flex items-center group relative z-10"
          >
            <img 
              src="/assets/img/logo-black.png" 
              alt="BotoHarmony" 
              className="h-8 md:h-10 w-auto object-contain transition-all duration-300 group-hover:scale-105" 
            />
          </Link>

          {/* Desktop Menu - Center */}
                    {/* Center - Menu */}
          <div className="flex items-center justify-center">
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to="/" 
                className="text-sm font-medium text-dark hover:text-secondary transition-colors relative group"
              >
                HOME
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link 
                to="/quem-somos" 
                className="text-sm font-medium text-dark hover:text-secondary transition-colors relative group"
              >
                QUEM SOMOS
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link 
                to="/services" 
                className="text-sm font-medium text-dark hover:text-secondary transition-colors relative group"
              >
                ESPECIALISTAS
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link 
                to="/results" 
                className="text-sm font-medium text-dark hover:text-secondary transition-colors relative group"
              >
                CASES
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link 
                to="/contact" 
                className="text-sm font-medium text-dark hover:text-secondary transition-colors relative group"
              >
                CONTATO
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </nav>
          </div>

          {/* CTA Button - Right */}
          <div className="flex items-center justify-end gap-4">
            <Link 
              to="/agendar" 
              className="hidden lg:flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-secondary to-primary text-white text-xs font-bold tracking-wider uppercase rounded-full shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 group relative overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-primary to-secondary transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
              <span className="relative z-10">AGENDAR CONSULTA</span>
              <svg 
                className="relative z-10 w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden relative w-10 h-10 flex items-center justify-center text-gray-700 hover:text-secondary focus:outline-none transition-colors duration-300"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-5 flex flex-col justify-between">
                <span 
                  className={`w-full h-0.5 bg-current transform transition-all duration-300 origin-center ${
                    isMenuOpen ? 'rotate-45 translate-y-2' : ''
                  }`}
                />
                <span 
                  className={`w-full h-0.5 bg-current transition-all duration-300 ${
                    isMenuOpen ? 'opacity-0 scale-0' : ''
                  }`}
                />
                <span 
                  className={`w-full h-0.5 bg-current transform transition-all duration-300 origin-center ${
                    isMenuOpen ? '-rotate-45 -translate-y-2' : ''
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isMenuOpen ? 'max-h-96 opacity-100 mt-6' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col space-y-4 pb-6 pt-4 border-t border-gray-200">
            {navItems.map((item, index) => (
              <Link
                key={item.id}
                to={item.path}
                className={`text-sm font-semibold tracking-wider uppercase transition-all duration-300 transform hover:translate-x-2 ${
                  isActive(item.path)
                    ? 'text-secondary'
                    : 'text-gray-700 hover:text-secondary'
                }`}
                onClick={() => setIsMenuOpen(false)}
                style={{ 
                  transitionDelay: isMenuOpen ? `${index * 50}ms` : '0ms',
                  opacity: isMenuOpen ? 1 : 0,
                  transform: isMenuOpen ? 'translateX(0)' : 'translateX(-20px)'
                }}
              >
                {item.name}
              </Link>
            ))}
            <Link 
              to="/contato" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary to-primary text-white text-sm font-bold tracking-wider uppercase rounded-full shadow-md hover:shadow-xl transition-all duration-300 mt-2"
              onClick={() => setIsMenuOpen(false)}
            >
              <span>AGENDAR CONSULTA</span>
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};


export default Navbar;